import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Empty, Typography, Input, Select, Space, Badge, Skeleton } from 'antd'
import { SearchOutlined, FilterOutlined, CalendarOutlined } from '@ant-design/icons'
import '../../../styling/ResultsHistory.css'
import ResultDetail from '../ResultDetail/ResultDetail'
import ResultListItem from './components/ResultListItem'

const { Text, Title } = Typography
const { Option } = Select

const ResultsHistory = ({ onViewDetail, onBackFromDetail, isViewingDetail }) => {
  // CARESYNC IMPORTANT HOW TO REFERENCE AVAILABLE TESTS RESULTS
  const careSyncTestResults = useSelector((state) => state.careSyncTestResults.data)
  // CARESYNC IMPORTANT HOW TO REFERENCE AVAILABLE DATA POINTS (TEMPLATES FOR TESTS)
  const careSyncDataPoints = useSelector((state) => state.careSyncDataPoints.data)
  // CARESYNC IMPORTANT HOW TO REFERENCE AVAILABLE TESTS - NOTE KEY WILL MATCH THE DATA POINT
  const [selectedResult, setSelectedResult] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFilter, setDateFilter] = useState('all')
  const isLoading = !careSyncTestResults || !careSyncDataPoints

  // Process and filter test results
  const filteredResults = useMemo(() => {
    if (isLoading) return []

    return [...careSyncTestResults]
      .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
      .filter((result) => {
        // Find corresponding data point
        const dataPoint = careSyncDataPoints.find((dp) => dp.key === result.key)
        if (!dataPoint) return false

        // Apply filters
        const matchesSearch = dataPoint.label.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesDate = dateFilter === 'all' || filterByDate(result.dateCreated, dateFilter)
        return matchesSearch && matchesDate
      })
      .map((result) => ({
        ...result,
        dataPoint: careSyncDataPoints.find((dp) => dp.key === result.key),
        name: careSyncDataPoints.find((dp) => dp.key === result.key)?.label,
        value: result.value,
        unit: careSyncDataPoints.find((dp) => dp.key === result.key)?.suffix
      }))
  }, [careSyncTestResults, careSyncDataPoints, searchQuery, dateFilter, isLoading])

  // Filter test results by date
  const filterByDate = (date, filter) => {
    const resultDate = new Date(date)
    const now = new Date()
    const monthDiff = (now.getFullYear() - resultDate.getFullYear()) * 12 + now.getMonth() - resultDate.getMonth()

    switch (filter) {
      case '1month':
        return monthDiff <= 1
      case '3months':
        return monthDiff <= 3
      case '6months':
        return monthDiff <= 6
      default:
        return true
    }
  }

  // View result details
  const handleViewDetail = (result, index) => {
    setSelectedResult(result)
    setSelectedIndex(index)
    if (onViewDetail) {
      onViewDetail()
    }
  }

  // Handle back from detail
  const handleBackFromDetail = () => {
    setSelectedResult(null)
    setSelectedIndex(null)
    if (onBackFromDetail) {
      onBackFromDetail()
    }
  }

  // Navigate through results
  const handleNext = () => {
    if (selectedIndex < filteredResults.length - 1) {
      const nextIndex = selectedIndex + 1
      setSelectedIndex(nextIndex)
      setSelectedResult(filteredResults[nextIndex])
    }
  }

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1
      setSelectedIndex(prevIndex)
      setSelectedResult(filteredResults[prevIndex])
    }
  }

  // Group results by month/year
  const groupedResults = useMemo(() => {
    if (filteredResults.length === 0) return {}

    const groups = {}

    filteredResults.forEach((result) => {
      const date = new Date(result.dateCreated)
      const month = date.toLocaleString('default', { month: 'long' })
      const year = date.getFullYear()
      const key = `${month} ${year}`

      if (!groups[key]) {
        groups[key] = []
      }

      groups[key].push(result)
    })

    return groups
  }, [filteredResults])

  if (selectedResult) {
    return (
      <ResultDetail
        result={selectedResult}
        onBack={handleBackFromDetail}
        onNext={handleNext}
        onPrevious={handlePrevious}
        hasNext={selectedIndex < filteredResults.length - 1}
        hasPrevious={selectedIndex > 0}
      />
    )
  }

  return (
    <div className='results-history'>
      <div className='results-header'>
        <Title level={4}>Test Results</Title>
        <Text type='secondary'>View your health test results</Text>
      </div>

      <div className='results-filters'>
        <Input
          className='results-search'
          placeholder='Search tests...'
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
        />

        <Space className='filter-selector'>
          <Select defaultValue='all' onChange={setDateFilter} suffixIcon={<FilterOutlined />} className='date-filter'>
            <Option value='all'>All Time</Option>
            <Option value='1month'>Last Month</Option>
            <Option value='3months'>Last 3 Months</Option>
            <Option value='6months'>Last 6 Months</Option>
          </Select>
        </Space>
      </div>

      <div className='results-list-container'>
        {isLoading ? (
          <div className='results-loading'>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} active paragraph={{ rows: 2 }} className='result-skeleton' />
            ))}
          </div>
        ) : filteredResults.length > 0 ? (
          Object.entries(groupedResults).map(([dateGroup, results]) => (
            <div key={dateGroup} className='results-group'>
              <div className='results-group-header'>
                <CalendarOutlined />
                <Text>{dateGroup}</Text>
                <Badge count={results.length} className='group-count' />
              </div>

              {results.map((result, index) => (
                <ResultListItem
                  key={result._id || index}
                  result={result}
                  dataPoint={result.dataPoint}
                  onViewDetail={() =>
                    handleViewDetail(
                      result,
                      filteredResults.findIndex((r) => r._id === result._id)
                    )
                  }
                />
              ))}
            </div>
          ))
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery ? 'No matching results found' : 'No test results available yet'}
            className='no-results'
          />
        )}
      </div>
    </div>
  )
}

export default ResultsHistory
